import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTable, useSortBy, useGlobalFilter, usePagination, useRowSelect, useExpanded } from 'react-table';

import { Autocomplete, Checkbox, Div, Icon, Input, Label, Text } from '@pinpoint/design-kit';

import Button from 'src/components/button';

/* eslint-disable react/prop-types */

function GlobalFilter({ globalFilter, setGlobalFilter }) {
  return (
    <Div w="400px" d="flex" align="center" justify="center" m="auto" p={{ t: '1rem', b: '3rem' }}>
      <Input
        p={{ x: '1.5rem', y: '.5rem' }}
        type="text"
        name="filter"
        value={globalFilter || ''}
        onChange={(e) => {
          setGlobalFilter(e.target.value || undefined);
        }}
        rounded={{ l: 'sm' }}
        suffix={
          <Button buttonStyle="primary" rounded={{ r: 'sm' }}>
            <Icon name="Search" color="gray300" />
          </Button>
        }
      />
    </Div>
  );
}

const SelectColumn = {
  id: 'selection',
  groupByBoundary: true,
  Header: () => <Div>Select</Div>,
  Cell: ({ row }) => {
    const { checked } = row.getToggleRowSelectedProps();
    return (
      <Div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const { checked: _checked, onRowSelect, onRowDeselect } = row.getToggleRowSelectedProps();
          if (!_checked) {
            onRowSelect(row);
          } else {
            onRowDeselect(row);
          }
        }}
      >
        <Label>
          <Checkbox checked={checked} />
        </Label>
      </Div>
    );
  },
};

export const ExpanderColumn = {
  id: 'expander',
  Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
    <Div tag="span" {...getToggleAllRowsExpandedProps()}>
      {isAllRowsExpanded ? <Icon name="DownArrow" /> : <Icon name="RightArrow" />}
    </Div>
  ),
  Cell: ({ row }) =>
    row.canExpand ? (
      <Div
        w="100%"
        tag="span"
        {...row.getToggleRowExpandedProps()}
        onClick={(e) => {
          const { onClick: _onClick } = row.getToggleRowExpandedProps();
          e.preventDefault();
          e.stopPropagation();
          _onClick(e);
        }}
      >
        {row.isExpanded ? <Icon name="DownArrow" /> : <Icon name="RightArrow" />}
      </Div>
    ) : null,
};

function StyledTableHeadingCell(props) {
  return <Div {...props} tag="th" />;
}

function TableHeadingCell({ children, ...rest }) {
  return (
    <StyledTableHeadingCell
      d="inline-grid"
      bg="gray300"
      textColor="brand500"
      w="calc(100%)"
      h="calc(100% - .5rem)"
      p={{ x: '1.5rem', y: '1rem' }}
      m={{ r: '1rem', b: '.5rem' }}
      textAlign="left"
      textTransform="capitalize"
      {...rest}
    >
      {children}
    </StyledTableHeadingCell>
  );
}

function BaseStyledRow(props) {
  return <Div {...props} tag="tr" />;
}
const StyledRow = styled(BaseStyledRow)`
  & > td {
    background-color: ${(props) => (props.depth === 0 ? '#F8F8F8' : '#FAFBFB')};
  }
`;

function Row({ children, ...rest }) {
  return (
    <StyledRow d="contents" {...rest}>
      {children}
    </StyledRow>
  );
}

function StyledCell(props) {
  return <Div {...props} tag="td" />;
}

function Cell({ children, depth, ...rest }) {
  return (
    <StyledCell
      d="inline-grid"
      w="100%"
      h="calc(100% - 1rem)"
      m={{ y: '.5rem' }}
      p={{ x: `${1.5 + depth / 2}rem`, y: '0.5rem' }}
      {...rest}
    >
      {children}
    </StyledCell>
  );
}

function Pagination({
  canPreviousPage,
  canNextPage,
  pageOptions,
  pageCount,
  gotoPage,
  nextPage,
  previousPage,
  setPageSize,
  pageIndex,
  pageSize,
}) {
  const [page, setPage] = useState(pageIndex + 1);
  const [isOpen, setIsOpen] = useState(false);
  const RECORDS = ['10', '20', '30', '40', '50'];
  return (
    <Div d="flex" justify="space-between" align="center" m={{ y: '0.5rem' }}>
      <Div d="flex" m={{ x: '-0.5rem' }}>
        <Button
          buttonStyle="primary"
          disabled={!canPreviousPage}
          onClick={() => {
            gotoPage(0);
          }}
          data-tip="first"
        >
          <Icon color="gray300" name="Back" />
        </Button>
        <Button
          buttonStyle="primary"
          disabled={!canPreviousPage}
          onClick={() => {
            previousPage();
          }}
          data-tip="previous"
        >
          <Icon color="gray300" name="LeftArrow" />
        </Button>
        <Button
          buttonStyle="primary"
          disabled={!canNextPage}
          onClick={() => {
            nextPage();
          }}
          data-tip="next"
        >
          <Icon color="gray300" name="RightArrow" />
        </Button>
        <Button
          buttonStyle="primary"
          disabled={!canNextPage}
          onClick={() => {
            gotoPage(pageCount - 1);
          }}
          data-tip="last"
        >
          <Icon color="gray300" name="Next" />
        </Button>
      </Div>
      <Div d="flex" align="center">
        <Text>{`Page ${pageIndex + 1} of ${pageOptions.length}`}</Text>
      </Div>
      <Div d="flex" align="center">
        <Text p={{ r: '0.5rem' }}>Go To Page</Text>
        <Input
          w="4rem"
          type="number"
          value={page}
          onChange={(e) => {
            setPage(e.target.value);
          }}
        />
        <Button
          buttonStyle="primary"
          disabled={Number(page) < 0 || Number(page) > pageCount}
          onClick={() => {
            gotoPage(Number(page) - 1);
          }}
          data-tip="jump"
        >
          <Text color="gray300">Go</Text>
        </Button>
      </Div>
      <Div d="flex" align="center">
        <Text p={{ r: '0.5rem' }}>Show </Text>
        <Autocomplete
          w="8rem"
          hoverBg="gray200"
          focusBg="gray200"
          bg="white"
          isOpen={isOpen}
          value={pageSize.toString()}
          onClick={() => setIsOpen(!isOpen)}
          menuOptions={{
            pos: 'absolute',
            shadow: 4,
            minW: '100%',
            rounded: 'md',
            maxH: '250px',
            bg: 'white',
            overflow: 'auto',
          }}
          menu={RECORDS}
          onSelection={(index) => {
            setPageSize(RECORDS[index]);
          }}
        />
        <Text p={{ l: '0.5rem' }}>records per page</Text>
      </Div>
    </Div>
  );
}

function StyledTable(props) {
  return <Div {...props} tag="table" />;
}

function Table({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  rows,
  prepareRow,
  onRowClick,
  onRowSelect,
  onRowDeselect,
  hasExpansion,
  numCols,
  paginate,
  canPreviousPage,
  canNextPage,
  pageOptions,
  pageCount,
  gotoPage,
  nextPage,
  previousPage,
  setPageSize,
  state,
  ...rest
}) {
  return (
    <>
      <StyledTable
        {...getTableProps()}
        d="grid"
        justify="left"
        align="center"
        minW="100%"
        p={{ x: '1.5rem', y: '.5rem' }}
        overflow="auto"
        style={{
          gridTemplateColumns: `repeat(${numCols || '1fr'}, 1fr)`,
        }}
        {...rest}
      >
        <thead style={{ display: 'contents' }}>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} style={{ display: 'contents' }}>
              {headerGroup.headers.map((column) => (
                <TableHeadingCell {...column.getHeaderProps(column.getSortByToggleProps())}>
                  <Div d="flex" align="center">
                    {column.render('Header')}
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <Icon size="1rem" name="DownArrow" />
                      ) : (
                        <Icon size="1rem" name="UpArrow" />
                      )
                    ) : (
                      ''
                    )}
                  </Div>
                </TableHeadingCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody style={{ display: 'contents' }} {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <Row onClick={() => {}} {...row.getRowProps()} depth={row.depth}>
                {row.cells.map((cell, i) => {
                  const { subRowsColsIndentIndexes, ...cellProps } = cell.getCellProps();
                  return (
                    <Cell {...cellProps} depth={subRowsColsIndentIndexes.includes(i) ? row.depth : 0}>
                      <Div d="flex" align="center" justify="start">
                        {cell.render('Cell')}
                      </Div>
                    </Cell>
                  );
                })}
              </Row>
            );
          })}
        </tbody>
      </StyledTable>
      {paginate ? (
        <Pagination
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageOptions={pageOptions}
          pageCount={pageCount}
          gotoPage={gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          setPageSize={setPageSize}
          pageIndex={state.pageIndex}
          pageSize={state.pageSize}
        />
      ) : null}
    </>
  );
}

function Grid({
  columns,
  data,
  gridState,
  controlledGridState,
  onRowClick,
  onRowSelect,
  onRowDeselect,
  hasGlobalFilter,
  hasSelection,
  hasExpansion,
  setGridState,
  subRowsColsIndentIndexes,
  paginate,
  ...rest
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      selectSubRows: false,
      initialState: gridState,
      stateReducer: (_state) => {
        const newState = {
          ..._state,
          ...controlledGridState,
        };
        setGridState(newState);
        return newState;
      },
      useControlledState: (_state) =>
        useMemo(
          () => ({
            ..._state,
            ...controlledGridState,
          }),
          // NOTE: extra insurance in the memo
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [_state, controlledGridState]
        ),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.getToggleRowSelectedProps.push({
        onRowSelect: (row) => {
          onRowSelect(row);
        },
        onRowDeselect: (row) => {
          onRowDeselect(row);
        },
      });
      hasSelection &&
        hooks.visibleColumns.push((_columns) => {
          const hasExpander = _columns[0].id === 'expander';
          if (hasExpander) {
            const [expanderColumn, ..._rest] = _columns;
            return [expanderColumn, SelectColumn, ..._rest];
          }
          return [SelectColumn, ..._columns];
        });
      hooks.getCellProps.push({
        subRowsColsIndentIndexes,
      });
    }
  );
  return data && data.length > 0 ? (
    <Div>
      {hasGlobalFilter ? <GlobalFilter globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} /> : null}
      <Table
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        rows={paginate ? page : rows}
        prepareRow={prepareRow}
        onRowClick={({ original }) => {
          onRowClick(original);
        }}
        onRowSelect={({ original }) => {
          onRowSelect(original);
        }}
        onRowDeselect={({ original }) => {
          onRowDeselect(original);
        }}
        numCols={hasSelection ? 1 + columns.length : columns.length}
        hasExpansion
        state={state}
        paginate={paginate}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageOptions={pageOptions}
        pageCount={pageCount}
        gotoPage={gotoPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        {...rest}
      />
    </Div>
  ) : (
    <Div>No Data</Div>
  );
}

Grid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  gridState: PropTypes.shape({}),
  controlledGridState: PropTypes.shape({}),
  onRowClick: PropTypes.func,
  onRowSelect: PropTypes.func,
  onRowDeselect: PropTypes.func,
  hasGlobalFilter: PropTypes.bool,
  hasSelection: PropTypes.bool,
  hasExpansion: PropTypes.bool,
  setGridState: PropTypes.func,
  subRowsColsIndentIndexes: PropTypes.arrayOf(PropTypes.number),
  paginate: PropTypes.bool,
};

Grid.defaultProps = {
  gridState: {},
  controlledGridState: {},
  onRowClick: () => null,
  onRowSelect: () => null,
  onRowDeselect: () => null,
  hasGlobalFilter: false,
  hasSelection: false,
  hasExpansion: false,
  setGridState: () => null,
  subRowsColsIndentIndexes: [],
  paginate: false,
};

export default Grid;
